import { useFormikContext } from 'formik'
import { useEffect, useMemo, useState } from 'react'
import type React from 'react'
import { createPortal } from 'react-dom'
import { CheckIcon, HelpCircleIcon } from '../shared/PaymentIcons'
import { Tooltip } from '../shared/Tooltip'
import type { PaymentsFormValues } from '../shared/validationSchema'
import {
  ghostBtnStyle,
  modalBodyStyle,
  modalFooterStyle,
  modalHeaderStyle,
  modalOverlayStyle,
  modalShellStyle,
  primaryBtnStyle,
} from './dialogTheme'
import {
  errorStyle,
  inputStyle,
  labelStyle,
} from './providerFields/dialogFieldStyles'

interface Props {
  open: boolean
  // Clicking the primary "Save" button ONLY pushes field values into the parent
  // Formik context and closes the dialog — it does NOT call `submitForm()` and
  // does NOT enable the method. Persistence happens through the outer save
  // surface: the Payments tab's "Save changes" footer (`UnsavedChangesPanel`)
  // or the AddLocation wizard's "Save & Next" button.
  onClose: () => void
}

interface InteracDraft {
  recipient_email: string
  security_question: string
  security_answer: string
  additional_notes: string
}

interface InteracDraftErrors {
  recipient_email?: string
  security_question?: string
  security_answer?: string
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const validateDraft = (draft: InteracDraft): InteracDraftErrors => {
  const errors: InteracDraftErrors = {}
  const email = draft.recipient_email.trim()
  if (!email) {
    errors.recipient_email = 'Please enter a Recipient Email'
  } else if (!EMAIL_REGEX.test(email)) {
    errors.recipient_email = 'Invalid email format'
  }
  if (!draft.security_question.trim()) {
    errors.security_question = 'Please enter a Security Question'
  }
  if (!draft.security_answer.trim()) {
    errors.security_answer = 'Please enter a Security Answer'
  }
  return errors
}

const helperLabelStyle: React.CSSProperties = {
  ...labelStyle,
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  marginBottom: 6,
}

const closeBtnStyle: React.CSSProperties = {
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
  padding: 4,
  lineHeight: 0,
  color: '#6B7280',
}

interface FieldMeta {
  name: keyof InteracDraft
  label: string
  placeholder: string
  tooltip: string
  type: 'email' | 'text' | 'textarea'
}

const FIELDS: FieldMeta[] = [
  {
    name: 'recipient_email',
    label: 'Recipient Email',
    placeholder: 'payments@yourstore.ca',
    tooltip:
      'The email address linked to your Interac e-Transfer account. Customers will send payments to this email.',
    type: 'email',
  },
  {
    name: 'security_question',
    label: 'Security Question',
    placeholder: 'e.g., What is the store name?',
    tooltip:
      'The security question customers must answer when sending the e-Transfer. Keep it simple.',
    type: 'text',
  },
  {
    name: 'security_answer',
    label: 'Security Answer',
    placeholder: 'e.g., BreadstackStore',
    tooltip:
      'The answer customers need to provide. This will be shown to the customer at checkout.',
    type: 'text',
  },
  {
    name: 'additional_notes',
    label: 'Additional Notes',
    placeholder: 'e.g., Please include your order number in the message field.',
    tooltip: 'Optional instructions shown to customers at checkout.',
    type: 'textarea',
  },
]

export const InteracConfigDialog: React.FC<Props> = ({ open, onClose }) => {
  const { values, setFieldValue } = useFormikContext<PaymentsFormValues>()
  const interac = values.payments.interac_e_transfer

  const seed = useMemo<InteracDraft>(
    () => ({
      recipient_email: interac.recipient_email ?? '',
      security_question: interac.security_question ?? '',
      security_answer: interac.security_answer ?? '',
      additional_notes: interac.additional_notes ?? '',
    }),
    [
      interac.recipient_email,
      interac.security_question,
      interac.security_answer,
      interac.additional_notes,
    ],
  )

  const [draft, setDraft] = useState<InteracDraft>(seed)
  const [touched, setTouched] = useState<Record<keyof InteracDraft, boolean>>({
    recipient_email: false,
    security_question: false,
    security_answer: false,
    additional_notes: false,
  })

  useEffect(() => {
    if (open) {
      setDraft(seed)
      setTouched({
        recipient_email: false,
        security_question: false,
        security_answer: false,
        additional_notes: false,
      })
    }
  }, [open, seed])

  if (!open) return null
  if (typeof document === 'undefined') return null

  const errors = validateDraft(draft)
  const hasErrors = Object.keys(errors).length > 0

  const isConfigured =
    !!interac.recipient_email?.trim() &&
    !!interac.security_question?.trim() &&
    !!interac.security_answer?.trim()
  const title = isConfigured ? 'Edit Interac' : 'Configure Interac'

  // Saving only stages the config fields into the parent Formik state and
  // closes — no `submitForm()` (no API call) and no auto-enable. The outer save
  // surface (Payments tab "Save changes" footer or wizard "Save & Next")
  // persists the change, and `enabled` is set only by toggling a fulfillment
  // channel in `InteracETransferSection`.
  const handleSave = async () => {
    setTouched({
      recipient_email: true,
      security_question: true,
      security_answer: true,
      additional_notes: true,
    })
    if (hasErrors) return

    await Promise.all([
      setFieldValue(
        'payments.interac_e_transfer.recipient_email',
        draft.recipient_email.trim(),
      ),
      setFieldValue(
        'payments.interac_e_transfer.security_question',
        draft.security_question.trim(),
      ),
      setFieldValue(
        'payments.interac_e_transfer.security_answer',
        draft.security_answer.trim(),
      ),
      setFieldValue(
        'payments.interac_e_transfer.additional_notes',
        draft.additional_notes,
      ),
    ])
    onClose()
  }

  const handleFieldChange =
    (name: keyof InteracDraft) =>
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const next = e.target.value
      setDraft((prev) => ({ ...prev, [name]: next }))
    }

  const handleFieldBlur = (name: keyof InteracDraft) => () => {
    setTouched((prev) => ({ ...prev, [name]: true }))
  }

  const renderField = (def: FieldMeta) => {
    const value = draft[def.name]
    const fieldErr = (errors as Record<string, string | undefined>)[def.name]
    const showError = !!fieldErr && touched[def.name]
    const fieldId = `interac__dialog__${def.name}`
    return (
      <div key={def.name}>
        <label htmlFor={fieldId} style={helperLabelStyle}>
          {def.label}
          <Tooltip text={def.tooltip}>
            <HelpCircleIcon size={12} color="#9CA3AF" />
          </Tooltip>
        </label>
        {def.type === 'textarea' ? (
          <textarea
            id={fieldId}
            value={value}
            placeholder={def.placeholder}
            rows={3}
            onChange={handleFieldChange(def.name)}
            onBlur={handleFieldBlur(def.name)}
            style={{ ...inputStyle, resize: 'vertical', minHeight: 72 }}
          />
        ) : (
          <input
            id={fieldId}
            type={def.type}
            value={value}
            placeholder={def.placeholder}
            onChange={handleFieldChange(def.name)}
            onBlur={handleFieldBlur(def.name)}
            style={inputStyle}
          />
        )}
        {showError && <p style={errorStyle}>{fieldErr}</p>}
      </div>
    )
  }

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      style={modalOverlayStyle}
    >
      <div style={modalShellStyle}>
        <div style={modalHeaderStyle}>
          <h3
            style={{
              fontSize: 15,
              fontWeight: 700,
              color: '#111827',
              margin: 0,
            }}
          >
            {title}
          </h3>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            style={closeBtnStyle}
          >
            <svg
              width="18"
              height="18"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
            >
              <path d="M6 6l12 12M6 18L18 6" strokeLinecap="round" />
            </svg>
          </button>
        </div>

        <div style={modalBodyStyle}>{FIELDS.map(renderField)}</div>

        <div style={modalFooterStyle}>
          <button type="button" onClick={onClose} style={ghostBtnStyle}>
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            style={{
              ...primaryBtnStyle,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
            }}
          >
            <CheckIcon size={13} color="#fff" />
            Save
          </button>
        </div>
      </div>
    </div>,
    document.body,
  )
}
